import React, { useState, useRef, useEffect } from "react";
import ServicesPage from "./ServicesPage";
import AboutPage from "./AboutPage";
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from "react-router-dom";
import ScrollToTop from "./ScrollToTop";
import { Helmet } from "react-helmet";

// add this import so Tailwind/App.css is loaded
import "./App.css";
export { Header, Footer, CTA, Clarity };


function HeadMeta() {
  return (
    <Helmet>
      {/* Titel & Beschreibung */}
      <title>JUMIBI Consulting UG</title>
      <meta name="description" content="Software Consulting.AI-driven Invoice Scanning, Workflow Automation, and Dispute Management Tool." />

      {/* Open Graph / LinkedIn / Facebook */}
      <meta property="og:title" content="JUMIBI Consulting UG" />
      <meta property="og:description" content="Software Consulting. AI-driven Invoice Scanning, Workflow Automation, and Dispute Management Tool." />
      <meta property="og:image" content="https://yellow-mud-0afbe2610.6.azurestaticapps.net/images/video-poster.png" />
      <meta property="og:url" content="https://www.jumibi.de" />
      <meta property="og:type" content="website" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="JUMIBI Consulting" />
      <meta name="twitter:description" content="Software Consulting. AI-driven Invoice Scanning, Workflow Automation, and Dispute Management Tool." />
      <meta name="twitter:image" content="https://yellow-mud-0afbe2610.6.azurestaticapps.net/images/video-poster.png" />
    </Helmet>
  );
}

function Clarity() {
  useEffect(() => {
    (function(c,l,a,r,i,t,y){
      c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
      t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
      y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", "tie2eg76cw");
  }, []);

  return null;
}


function JsonLd() {
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "DocInsight AI Invoice Platform",
      "url": "https://www.jumibi.de",
      "logo": "https://www.jumibi.de/images/cropped-Logo_J_inkludiert-removebg-preview_snipped.png",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web",
      "description": "AI-driven Invoice Scanning, Workflow Automation, and Dispute Management Tool. Optimizes accounting processes, automates invoice extraction, and accelerates dispute resolution.",
      "softwareVersion": "1.0",
      "featureList": [
        "AI Invoice Extraction",
        "Accounting Optimization Workflow",
        "Dispute Approval Tool",
        "Fast Implementation in 7 days",
        "No ERP licenses required"
      ],
      "offers": {
        "@type": "Offer",
        "price": "Contact for pricing",
        "priceCurrency": "EUR",
        "url": "mailto:info@jumibi.de?subject=DocInsights%20Demo%20Request"
      }
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return null;
}
// ---------------- Consent-Banner ----------------
function ConsentBanner({ onAccept, onDecline }) {
  const [lang, setLang] = useState("de");

  useEffect(() => {
    const browserLang = navigator.language || navigator.userLanguage;
    setLang(browserLang.startsWith("en") ? "en" : "de");
  }, []);

  const texts = {
    de: {
      title: "Erleben Sie die Website in voller Funktionalität!",
      message:
        "Nur mit Ihrer Zustimmung zu Microsoft Clarity können wir die Website für Sie optimal darstellen, Ladezeiten minimieren und Inhalte personalisieren. Ihre Daten bleiben anonym.",
      accept: "Jetzt erleben",
      decline: "Später",
    },
    en: {
      title: "Unlock the full experience!",
      message:
        "Only by consenting to Microsoft Clarity can we optimize the website for you, reduce loading times, and personalize content. Your data remains anonymous.",
      accept: "Experience Now",
      decline: "Later",
    },
  };

  const t = texts[lang];

  return (
    <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-white border border-gray-300 rounded-xl shadow-lg p-6 max-w-3xl w-[90%] flex flex-col md:flex-row justify-between items-center z-50 space-y-4 md:space-y-0 md:space-x-6 animate-slideUp">
      <div className="text-gray-800 text-center md:text-left">
        <p className="font-medium mb-1">{t.title}</p>
        <p className="text-gray-600 text-sm">{t.message}</p>
      </div>
      <div className="flex space-x-3">
        <button
          onClick={onAccept}
          className="bg-blue-600 text-white px-5 py-2 rounded-xl shadow hover:bg-blue-700 transition"
        >
          {t.accept}
        </button>
        <button
          onClick={onDecline}
          className="bg-gray-200 text-gray-800 px-5 py-2 rounded-xl shadow hover:bg-gray-300 transition"
        >
          {t.decline}
        </button>
      </div>
    </div>
  );
}



function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation(); // Get the current URL path

  const isActive = (path) => pathname === path; // Check if the path matches the current URL

useEffect(() => {
  if (!mobileOpen) {
    let targetId = null;
    if (pathname === "/") {
      targetId = "home";
    } else if (pathname === "/services-page") {
      targetId = "ServiceHero";
    } else if (pathname === "/about") {
      targetId = "AboutHero";
    }

    if (targetId) {
      const el = document.getElementById(targetId);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        // fallback wenn section noch nicht gerendert ist
        setTimeout(() => {
          const retryEl = document.getElementById(targetId);
          retryEl?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 250);
      }
    }
  }
}, [pathname, mobileOpen]);




  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const mailtoLink = "mailto:info@jumibi.de?subject=DocInsights%20Demo%20Request";

  return (
<header className="site-header fixed top-0 left-0 w-full z-50 !bg-black text-white">
        <div className="header-inner container">
        <a href="#home" className="brand">
          <img
            src="./images/cropped-Logo_J_inkludiert-removebg-preview_snipped.png"
            alt="JUMIBI Consulting"
            className="custom-logo"
          />
        </a>

        {/* Desktop navigation */}
        <nav className="main-nav" aria-label="Main Navigation">
          <ul className="main-header-menu">
            <li>              <Link
                to="/"
                className={isActive("/") ? "active" : ""}
              >
                Home
              </Link></li>

            <li>              <Link
                to="/services-page"
                className={isActive("/services-page") ? "active" : ""}
              >
                Services
              </Link></li>
              <li>
              <a href={mailtoLink} className="hover:underline">
                Contact
              </a>
            </li>
            <li>              <Link
                to="/about"
                className={isActive("/about") ? "active" : ""}
              >
                About
              </Link></li>
          </ul>
        </nav>

        {/* Mobile toggle */}
        <button
          className="mobile-toggle"
          aria-label="Open menu"
          onClick={() => setMobileOpen(v => !v)}
        >
          <span className="hamburger" />
        </button>
      </div>

      {/* Mobile menu overlay */}
      {mobileOpen && (
<div
  className="jumibi-mobile-menu-overlay open bg-black"
  role="dialog"
  aria-modal="true"
  style={{ zIndex: 11000 }}
>
          <div className="jumibi-mobile-menu-inner container">
            <ul className="mobile-menu-list">
              <Link to="/" onClick={() => {setMobileOpen(false); }}> Home</Link>
              <Link to="/services-page" onClick={() => setMobileOpen(false)}>Services</Link>

              <Link to="/about" onClick={() => setMobileOpen(false)}>About</Link>
                            <li>
                <a
                  href={mailtoLink}
                  className="text-white hover:underline inline-block cursor-pointer bg-transparent p-0"
                  onClick={() => setMobileOpen(false)}
                >
                  Contact
                </a>
              </li>
            </ul>
          </div>
        </div>
      )}
    </header>
  );
}


function Hero() {
  const videoRef = useRef(null);
  const [poster, setPoster] = useState("");
  const [loading, setLoading] = useState(true); // Track loading state

  useEffect(() => {
    const video = document.createElement("video");
    video.src = "/videos/DocInsightMarketingVideo-1.mp4";
    video.muted = true;
    video.crossOrigin = "anonymous";

    const captureFrame = () => {
      if (video.readyState >= 2) {
        video.currentTime = 3;
      }
    };

    const onSeeked = () => {
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      setPoster(canvas.toDataURL("image/png"));
      setLoading(false); // Poster is ready
    };

    video.addEventListener("loadeddata", captureFrame, { once: true });
    video.addEventListener("seeked", onSeeked, { once: true });
    video.load();

    return () => {
      video.removeEventListener("loadeddata", captureFrame);
      video.removeEventListener("seeked", onSeeked);
    };
  }, []);

  return (
    <section
      id="home"
      className="relative h-screen flex flex-col items-center justify-center bg-gradient-to-r from-blue-600 to-indigo-700 text-white text-center"
    >
      <div className="max-w-3xl px-6 space-y-4">
        <h2 className="text-xl md:text-2xl font-semibold">
          Fastest And Most Lightweight Invoice and WF Tool
        </h2>
        <h1 className="text-4xl md:text-6xl font-bold leading-tight">
          AI driven Invoice Scanning, Workflow and E-Invoicing Platform!
        </h1>
        <h3 className="text-lg md:text-xl font-medium">
          Only 7 days for implementation and no ERP licenses!
        </h3>

<div className="mt-6 w-full relative">
  {loading && (
    <div className="absolute inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-10">
      <div className="loader border-4 border-t-4 border-blue-700 rounded-full w-12 h-12 animate-spin"></div>
    </div>
  )}
  {!loading && (
    <video
      ref={videoRef}
  className="w-full h-auto rounded-xl shadow-[0_10px_50px_rgba(0,0,0,0.7)] object-contain aspect-video"      src="/videos/DocInsightMarketingVideo-1.mp4"
      poster="./images/video-poster.png"
      width="1280"
      height="720"
      controls
      playsInline
      preload="metadata"
      controlsList="nodownload"
    />
  )}
</div>

      </div>
    </section>
    
  );
}



function Services() {
  return (
    <section id="services" className="py-20 bg-gray-50">
      <div className="max-w-6xl mx-auto px-6 text-center">
        <h2 className="text-3xl font-bold mb-4">Our Services beside DocInsight</h2>
        <p className="text-gray-600 max-w-2xl mx-auto mb-12">
          Beyond our AI Driven invoice tool, we help businesses streamline their IT landscape with tailored software solutions, seamless SAP integrations, and reliable system administration.
        </p>

        <div className="grid md:grid-cols-3 gap-8">
          <article className="bg-white p-6 rounded-xl shadow hover:shadow-lg transition">
            <h3 className="text-xl font-semibold mb-2">Software Development</h3>
            <p className="text-gray-600">We develop tailored applications to support your internal processes, customer interfaces, or integration needs: scalable, secure, and maintainable.</p>
          </article>

          <article className="bg-white p-6 rounded-xl shadow hover:shadow-lg transition">
            <h3 className="text-xl font-semibold mb-2">SAP Projects (Fiori, APIs, etc.)</h3>
            <p className="text-gray-600">We extend your existing SAP landscape through integrations, interfaces, and tailored workflows.</p>
          </article>

          <article className="bg-white p-6 rounded-xl shadow hover:shadow-lg transition">
            <h3 className="text-xl font-semibold mb-2">System Administration</h3>
            <p className="text-gray-600">We manage users, roles, and systems in your IT environments, from permissions to patching.</p>
          </article>
        </div>
      </div>
    </section>
  );
}



function CustomerLogos() {
  const logos = [
    "NEM-1.png",
    "Fasana-1.png",
    "mutares-1.jpg",
    "EFACEC-1.jpg",
    "Matikon-1.jpg",
    "SFC-1.png",
    "Consol-1.png",
    "HILO-Group-1.webp",
  ];

  return (
    <section className="customers">
      <div className="max-w-6xl mx-auto px-6">
        <h2 className="text-3xl font-bold text-center mb-8">Trusted by Our Customers</h2>
          <p className="text-gray-600 mb-8">Helping businesses streamline operations, boost efficiency, and achieve measurable results with tailored software solutions and smart automation.</p>

        <div className="overflow-hidden relative bg-white py-6 rounded-xl shadow-inner">
                    {/* container moving via animation */}
          <div className="flex flex-nowrap animate-scroll">
            {[...logos, ...logos].map((file, idx) => (
              <img
                key={idx}
                src={`./images/customers/${file}`}
                alt="Customer logo"
                className="h-20 mx-8 flex-shrink-0 object-contain"
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}


function Testimonials() {
  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-5xl mx-auto px-6 space-y-12">
        <div className="text-center">
          {/* Circular image above the testimonial */}
          <div className="relative mx-auto w-32 h-32 rounded-full overflow-hidden shadow-lg">
            <img
              src="./images/consol-susanne.png"
              alt="Susanne Feldhausen"
              className="w-full h-full object-cover"
            />
          </div>
          <h3 className="text-2xl font-semibold mt-6 mb-2">“A Game-Changer for Our IT and Business Efficiency”</h3>
          <blockquote className="bg-white p-6 rounded-xl shadow text-gray-700">
            <p>“We outsourced our entire IT to JUMIBI Consulting – an amazing job from start to finish. The service is outstanding, and the response time is impressively fast.”</p>
            <footer className="mt-4 text-sm text-gray-500">— Susanne Feldhausen, CEO, ConSol Dortmund</footer>
          </blockquote>
        </div>

        <div className="text-center">
                    {/* Circular image above the testimonial */}
          <div className="relative mx-auto w-32 h-32 rounded-full overflow-hidden shadow-lg">
            <img
              src="./images/paul.jpg"
              alt="Paul Robin"
              className="w-full h-full object-cover"
            />
          </div>
          <h3 className="text-2xl font-semibold mb-2">“Thanks to DocInsight, we were able to automate our invoice processing”</h3>
          <blockquote className="bg-white p-6 rounded-xl shadow text-gray-700">
            <p>“With DocInsight, we unified our invoice process across four locations and replaced scattered tools with one centralized solution.”</p>
            <footer className="mt-4 text-sm text-gray-500">— Paul Robin, Senior Manager Finance, Matikon</footer>
          </blockquote>
        </div>
      </div>
    </section>
  );
}

function CTA() {
  const phoneNumber = "+49 151 17610795";

  // Obfuscate number for crawlers
  const obfuscated = phoneNumber
    .split("")
    .map((c) => `&#${c.charCodeAt(0)};`)
    .join("");

  const [revealed, setRevealed] = useState(false);
  const sliderRef = useRef(null);
  const containerRef = useRef(null);
  const dragging = useRef(false);
  const startX = useRef(0);

  const onMouseDown = (e) => {
    dragging.current = true;
    startX.current = (e.clientX ?? (e.touches && e.touches[0].clientX)) || 0;
  };

  const onMove = (e) => {
    if (!dragging.current || revealed) return;
    const clientX = (e.clientX ?? (e.touches && e.touches[0].clientX)) || 0;
    const delta = clientX - startX.current;
    const slider = sliderRef.current;
    const container = containerRef.current;
    if (!slider || !container) return;
    const maxSlide = container.offsetWidth - slider.offsetWidth - 4;

    if (delta > 0) {
      slider.style.transform = `translateX(${Math.min(delta, maxSlide)}px)`;
      if (delta >= maxSlide) {
        setRevealed(true);
        dragging.current = false;
      }
    }
  };

  const onEnd = () => {
    if (!dragging.current) return;
    dragging.current = false;
    if (!revealed && sliderRef.current) {
      sliderRef.current.style.transform = "translateX(0)";
    }
  };

  return (
    <section className="py-12">
      <div className="max-w-4xl mx-auto px-6">
        <div className="bg-white p-8 rounded-xl shadow text-center">
          <h3 className="text-xl font-bold mb-2">Would you like to start a project with us?</h3>
          <p className="text-gray-600 mb-6">Ready to elevate your invoicing with DocInsight or interested in our consulting expertise? Let’s start the conversation — give us a call or send us a message.</p>

          <div
            ref={containerRef}
            className="relative mt-6 w-80 h-12 bg-blue-50 rounded-full shadow-lg overflow-hidden mx-auto cursor-pointer"
            onMouseMove={onMove}
            onMouseUp={onEnd}
            onMouseLeave={onEnd}
            onTouchMove={onMove}
            onTouchEnd={onEnd}
          >
            {!revealed ? (
              <div
                ref={sliderRef}
                className="absolute left-0 top-0 h-full w-32 bg-blue-600 text-white flex items-center justify-center font-semibold rounded-full shadow-md select-none"
                onMouseDown={onMouseDown}
                onTouchStart={onMouseDown}
                role="button"
                aria-label="Slide to call"
              >
                Slide to Call
              </div>
            ) : (
              <a
                href={`tel:${phoneNumber.replace(/\s/g, "")}`}
                className="absolute inset-0 flex items-center justify-center text-blue-700 font-semibold"
                dangerouslySetInnerHTML={{ __html: obfuscated }}
              />
            )}
          </div>

        </div>
      </div>
    </section>
  );
}


function Footer() {
  const mailtoLink = "mailto:info@jumibi.de?subject=DocInsights%20Demo%20Request";

  return (
    <footer className="bg-gray-900 text-gray-300 py-12">
      <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-4 gap-12">
        <div>
            <img
              src="/images/cropped-Logo_J_inkludiert-removebg-preview_snipped.png"
              alt="JUMIBI Consulting"
              className="footer-logo"
            />
   
          <p className="text-sm footer-philosophy">Direct. Fast. Personal. Intelligent.</p>
</div>
        <div>
          <h3 className="font-semibold text-white mb-3">Services</h3>
          <ul className="space-y-1 text-sm">
            <li><a href="#services" className="hover:underline">DocInsight powered by JUMIBI</a></li>
            <li><Link to="/services-page">Consulting Service</Link></li>
            <li><Link to="/services-page">Data & Carve-Out Projects</Link></li>
            <li><Link to="/services-page">Management & Ongoing Support</Link></li>
          </ul>
        </div>
        <div>
          <h3 className="font-semibold text-white mb-3">Business</h3>
          <ul className="space-y-1 text-sm">
            <li><Link to="/">Home</Link></li>
            <li><Link to="/services-page">Services</Link></li>
            <li><a href={mailtoLink} className="hover:underline">Contact</a></li>
            <li><Link to="/about">About</Link></li>

          </ul>
        </div>
        <div>
          <h3 className="font-semibold text-white mb-3">Get In Touch</h3>
          <address className="not-italic text-sm">
            Mike Mannz<br />
            Stuppstr. 16 50823 Köln<br />
            <a href={mailtoLink} className="hover:underline">info@jumibi.de</a><br />
          </address>
        </div>
      </div>

        <div className="mt-12 text-center text-xs text-gray-500">
          <p>Copyright © 2025 JUMIBI Consulting UG | All rights reserved.</p>
          <p>Imprint: JUMIBI Consulting UG (haftungsbeschränkt), Managing Director: Mike Mannz, Stuppstr. 16, 50823 Cologne, Germany, Registered at Local Court of Cologne (Amtsgericht Köln), HRB 111198</p>
        </div>
    </footer>
  );
}


function ScrollButton() {
  const [currentSection, setCurrentSection] = useState(null);

  useEffect(() => {
    const sections = document.querySelectorAll("section");
    const handleScroll = () => {
      let current = null;
      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        if (rect.top <= window.innerHeight / 2 && rect.bottom >= window.innerHeight / 2) {
          current = section.id;
        }
      });
      setCurrentSection(current);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToNextSection = () => {
    const sections = Array.from(document.querySelectorAll("section"));
    const currentScroll = window.scrollY;
    const nextSection = sections.find((section) => {
      const rect = section.getBoundingClientRect();
      return rect.top + currentScroll > currentScroll + window.innerHeight / 2;
    });

    if (nextSection) {
      nextSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <button
      className="scroll-button"
      onClick={scrollToNextSection}
      aria-label="Scroll to next section"
    >
      ↓
    </button>
  );
}

export default function App() {

  const [consent, setConsent] = useState(() => localStorage.getItem("clarity-consent") === "true");
  const [showBanner, setShowBanner] = useState(() => localStorage.getItem("clarity-consent") === null);

  const handleAccept = () => { setConsent(true); localStorage.setItem("clarity-consent", "true"); setShowBanner(false); };
  const handleDecline = () => { setConsent(false); localStorage.setItem("clarity-consent", "false"); setShowBanner(false); };


  return (
    <Router>
      <HeadMeta />
      <Clarity />
      <JsonLd />
      <Header />
      <ScrollButton />
      {showBanner && <ConsentBanner onAccept={handleAccept} onDecline={handleDecline} />}
      <Routes>
        {/* Main homepage */}
        <Route
          path="/"
          element={
            <main className="pt-20">
              <Hero />
              <Services />
              <CustomerLogos />
              <Testimonials />
              <CTA />
              <Footer />
            </main>
          }
        />

        {/* New services page */}
        <Route path="/about" element={<AboutPage />} />

        <Route path="/services-page" element={<ServicesPage />} />
      </Routes>
    </Router>
  );
}

